import { useEffect } from 'react';
import { useApolloClient, gql, InMemoryCache } from '@apollo/client';
import { fragments as staffFragments } from '@/graph/staff';
import { useWebSocketReconnect } from './useWebSocketReconnect';
import {
  markPollRefresh,
  markPollComplete,
  isWithinPollGracePeriod,
  isEventOlderThanPoll,
  isEventStale,
  getTimeSincePollRefresh,
  getEventPollAgeDiff,
  getEventAge,
  STALE_EVENT_GRACE_PERIOD_MS,
  MAX_EVENT_AGE_MS,
} from './useStaleEventDetection';

// Re-export staleness utilities for backwards compatibility
// Other modules may import these from useScheduleSubscription
export { markPollRefresh, markPollComplete, isWithinPollGracePeriod, isEventOlderThanPoll, isEventStale };

/**
 * Single subscription for entire daily schedule.
 * Optimized for readonly display screens (hallways, physician rooms).
 *
 * Receives batched updates for all procedures on a given date.
 * Updates Apollo cache directly for instant React re-renders.
 *
 * Benefits:
 * - 1 WebSocket connection (vs 128 for per-procedure)
 * - Efficient for multiple readonly screens
 * - Batched updates reduce network traffic
 * - Scales to unlimited display screens
 */

const SCHEDULE_UPDATED_INCREMENTAL_SUBSCRIPTION = gql`
  subscription ScheduleUpdatedIncremental($date: Date!, $physician: Long) {
    scheduleUpdatedIncremental(date: $date, physician: $physician) {
      id
      _fetchedAt
      or
      orId
      notes
      preOpNote
      anesthesiologistNotes
      orNotes
      procedureType
      procedurePriority
      procedureHstId
      physicianId
      physicianName
      physicianColor
      color
      isCanceled
      cancellationReason
      physician {
        id
        name
        color
      }
      anesthesiologistId
      anesthesiologistName
      anesthesiologist {
        id
        name
      }
      patient {
        id
        status
        bed
        events {
          admittedAt
          preOpAt
          readyForAnesthesiaAt
          spinalTimeoutAt
          readyForOrAt
          readyForSurgeonAt
          orAt
          orOutAt
          recoveryAt
          recoveryOutAt
          postOpAt
          postOpOutAt
          dischargedAt
          anestheticStartAt
          physicianInAt
          timeOutAt
          procedureStartAt
          procedureEndAt
          anestheticEndAt
          blockNerveActiveAt
          helpActiveAt
          flipRoomActiveAt
          holdProcedureAt
          dpuAt
        }
        room {
          id
          name
          type
        }
      }
      staffShifts {
        ...StaffShiftDataSchedule
      }
      assignedVendors {
        id
        vendor {
          id
          name
        }
      }
    }
  }
  ${staffFragments.shiftSchedule}
`;

interface ScheduleSubscriptionOptions {
  date: string;
  physician?: number;
  enabled?: boolean;
}

export function useScheduleSubscription({ date, physician, enabled = true }: ScheduleSubscriptionOptions) {
  const client = useApolloClient();
  const reconnectCount = useWebSocketReconnect(); // Detect reconnections

  useEffect(() => {
    if (!enabled || !date) {
      return;
    }

    if (reconnectCount > 0) {
      console.log(
        `[ScheduleSubscription] WebSocket reconnected (count: ${reconnectCount}), re-subscribing to schedule`
      );
    }

    const subscription = client
      .subscribe({
        query: SCHEDULE_UPDATED_INCREMENTAL_SUBSCRIPTION,
        variables: { date, physician },
      })
      .subscribe({
        next: ({ data }) => {
          if (!data?.scheduleUpdatedIncremental) {
            return;
          }

          // STALE EVENT DETECTION - Three layers of protection:

          // Layer 1: Ignore events while poll is in flight
          // This prevents race conditions during the poll request
          if (isWithinPollGracePeriod()) {
            const timeSincePoll = getTimeSincePollRefresh();
            console.log(
              `[ScheduleSubscription] Ignoring event during poll grace period (${Math.round(timeSincePoll)}ms since poll start, grace: ${STALE_EVENT_GRACE_PERIOD_MS}ms)`
            );
            return;
          }

          // Update can be single procedure or array of procedures
          const updates = Array.isArray(data.scheduleUpdatedIncremental)
            ? data.scheduleUpdatedIncremental
            : [data.scheduleUpdatedIncremental];

          const firstUpdate = updates[0];

          // Layer 2 (PRIMARY): Compare event's _fetchedAt against poll completion time
          // If event data was fetched BEFORE poll completed, poll has fresher data - reject
          // This handles EventBus delays of any duration (17-25s+ observed on view1-api)
          if (firstUpdate?._fetchedAt && isEventOlderThanPoll(firstUpdate._fetchedAt)) {
            const pollAge = getEventPollAgeDiff(firstUpdate._fetchedAt);
            console.log(
              `[ScheduleSubscription] Ignoring event older than poll (event fetched ${Math.round(pollAge)}ms before poll completed, procedures: ${updates.length})`
            );
            return;
          }

          // Layer 3 (FALLBACK): Check absolute event age for sanity
          // Rejects very old events when no poll has occurred or _fetchedAt missing
          if (firstUpdate?._fetchedAt && isEventStale(firstUpdate._fetchedAt)) {
            const eventAge = getEventAge(firstUpdate._fetchedAt);
            console.log(
              `[ScheduleSubscription] Ignoring stale event batch (age: ${Math.round(eventAge)}ms, max: ${MAX_EVENT_AGE_MS}ms, procedures: ${updates.length})`
            );
            return;
          }

          updates.forEach(procedure => {
            try {
              const cacheId = client.cache.identify({
                __typename: 'Procedure',
                id: procedure.id,
              });

              if (!cacheId) {
                return;
              }

              // CRITICAL: Use incoming data directly (TRUE ECST)
              // The subscription already contains complete procedure data
              const procedureData = {
                __typename: 'Procedure' as const,
                id: procedure.id,
                or: procedure.or,
                orId: procedure.orId,
                notes: procedure.notes,
                preOpNote: procedure.preOpNote,
                anesthesiologistNotes: procedure.anesthesiologistNotes,
                orNotes: procedure.orNotes,
                procedureType: procedure.procedureType,
                procedurePriority: procedure.procedurePriority,
                procedureHstId: procedure.procedureHstId,
                physicianId: procedure.physicianId,
                physicianName: procedure.physicianName,
                physicianColor: procedure.physicianColor,
                color: procedure.color,
                physician: procedure.physician,
                anesthesiologistId: procedure.anesthesiologistId,
                anesthesiologistName: procedure.anesthesiologistName,
                anesthesiologist: procedure.anesthesiologist,
                patient: procedure.patient,
                staffShifts: procedure.staffShifts,
                assignedVendors: procedure.assignedVendors,
              };

              // Update procedure in cache with writeFragment
              client.cache.writeFragment({
                id: cacheId,
                fragment: gql`
                  fragment UpdatedProcedure on Procedure {
                    id
                    or
                    orId
                    notes
                    preOpNote
                    anesthesiologistNotes
                    orNotes
                    procedureType
                    procedurePriority
                    procedureHstId
                    physicianId
                    physicianName
                    physicianColor
                    color
                    physician {
                      id
                      name
                      color
                    }
                    anesthesiologistId
                    anesthesiologistName
                    anesthesiologist {
                      id
                      name
                    }
                    patient {
                      id
                      status
                      bed
                      events {
                        admittedAt
                        preOpAt
                        readyForAnesthesiaAt
                        spinalTimeoutAt
                        readyForOrAt
                        readyForSurgeonAt
                        orAt
                        orOutAt
                        recoveryAt
                        recoveryOutAt
                        postOpAt
                        postOpOutAt
                        dischargedAt
                        anestheticStartAt
                        physicianInAt
                        timeOutAt
                        procedureStartAt
                        procedureEndAt
                        anestheticEndAt
                        blockNerveActiveAt
                        helpActiveAt
                        flipRoomActiveAt
                        holdProcedureAt
                        dpuAt
                      }
                      room {
                        id
                        name
                        type
                      }
                    }
                    staffShifts {
                      id
                      shiftState
                      staff {
                        id
                        staffId
                        name
                        titleAbbr
                        primarySpecialty
                        phoneNumber
                      }
                      from
                      to
                      additionalText
                      note
                      hourlyRate
                      openPosition {
                        primarySpecialty
                        otherSpecialties
                        title
                        description
                      }
                    }
                    assignedVendors {
                      id
                      vendor {
                        id
                        name
                      }
                    }
                  }
                `,
                data: procedureData,
                broadcast: true,
              });

              // Update patient entity cache for denormalization
              // denormalizeProcedure prefers procedure.patient when present,
              // falling back to entities.patients lookup only when absent
              // Defer update to next microtask to ensure it happens after entities query
              if (procedure.patient?.id) {
                const patientData = procedure.patient;
                const patientId = procedure.patient.id;

                queueMicrotask(() => {
                  const patientCacheId = client.cache.identify({
                    __typename: 'Patient',
                    id: patientId,
                  });

                  if (patientCacheId) {
                    try {
                      client.cache.writeFragment({
                        id: patientCacheId,
                        fragment: gql`
                          fragment UpdatedPatient on Patient {
                            id
                            status
                            bed
                            events {
                              admittedAt
                              preOpAt
                              readyForAnesthesiaAt
                              spinalTimeoutAt
                              readyForOrAt
                              readyForSurgeonAt
                              orAt
                              orOutAt
                              recoveryAt
                              recoveryOutAt
                              postOpAt
                              postOpOutAt
                              dischargedAt
                              anestheticStartAt
                              physicianInAt
                              timeOutAt
                              procedureStartAt
                              procedureEndAt
                              anestheticEndAt
                              blockNerveActiveAt
                              helpActiveAt
                              flipRoomActiveAt
                              holdProcedureAt
                              dpuAt
                            }
                            room {
                              id
                              name
                              type
                            }
                          }
                        `,
                        data: patientData,
                        broadcast: true,
                      });
                    } catch (_err) {
                      // Silently fail - cache update errors are non-critical
                    }
                  }
                });
              }

              // Trigger query re-execution by invalidating ROOT_QUERY fields
              // This forces Apollo to re-read from cache and notify watchers
              client.cache.modify({
                id: 'ROOT_QUERY',
                fields: {
                  procedures(existingProcedures) {
                    return existingProcedures;
                  },
                  dailyScheduleEntities(existingEntities) {
                    return existingEntities;
                  },
                },
                broadcast: true,
              });

              // Broadcast cache changes to all watchers
              if (client.cache instanceof InMemoryCache) {
                (client.cache as any).broadcastWatches();
              }
            } catch (err) {
              console.error(`[Schedule] ❌ Error updating cache for procedure ${procedure.id}:`, err);
            }
          });
        },
        error: err => {
          console.error('[ScheduleSubscription] Subscription error:', err);
        },
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [client, date, enabled, reconnectCount]);
}
